/**
 * @prop --logo-width: Width of logo
 * @prop --logo-height: Height of logo
 * @prop --logobar-background-color: Background color of logobar
 * @prop --stzh-header-logobar-background-color: **Global**: Background color of logobar
 *
 * @prop --stzh-header-height: **Global**: Height of header (readonly variable on `<html>`)
 * @prop --stzh-header-main-height: **Global**: Height of main header part (readonly variable on `<html>`)
 * @prop --stzh-header-metabar-height: **Global**: Height of metabar header (readonly variable on `<html>`)
 * @prop --stzh-header-logobar-height: **Global**: Height of logobar header (readonly variable on `<html>`)
 * @prop --stzh-header-is-stuck: **Global**: Whether head is currently sticky (readonly variable on `<html>`)
 * @prop --stzh-header-is-not-stuck: **Global**: Whether head is currently not sticky (readonly variable on `<html>`)
 */

:host {
  --logo-width: auto;
  --logo-height: 100%;
  --logobar-background-color: #{$headerLogobarBackgroundColor};

  @media print {
    display: none;
  }

  &[logo-type="aoz"],
  &[logo-type="pkzh"],
  &[logo-type="uvz"] {
    --logo-height: 31px;

    @include mq($from: medium) {
      --logo-height: 35px;
    }

    @include mq($from: ultra) {
      --logo-height: 53px;
    }
  }

  &[logo-type="vbz"] {
    --logo-height: 29px;

    @include mq($from: medium) {
      --logo-height: 53px;
    }

    @include mq($from: ultra) {
      --logo-height: 60px;
    }
  }

  &[logo-type="vbz"] ::slotted([slot="logo"]) {
    align-self: center;
  }

  ::slotted([slot="logo"]) {
    width: var(--logo-width);
    height: var(--logo-height);
  }
}


.stzh-header {
  @include fontSize('milli');
  color: $colorGrey90;

  &__inner,
  &__main {
    max-width: calc(100vw - var(--stzh-scrollbar-width));
    // transition-property: box-shadow;
    // transition-duration: $baseTransitionAnimationSpeed;
  }

  &__inner {
    @include mq($from: small) {
      position: relative;
      z-index: $zIndexHeader;
    }
  }

  &__vhidden {
    @include visuallyhidden;
  }

  &__main {
    display: flex;
    flex-direction: column;
    background-color: $colorWhite;

    @include mq($to: small) {
      position: relative;
      z-index: $zIndexHeader;
    }
  }

  &__metabar {
    background-color: $colorCoolgrey10;
  }

  &__metabar-inner {
    @include container;
    display: flex;
    align-items: center;
    height: $headerMetabarHeight;

    // @include mq($to: medium) {
    //   // overwrite container margin for regular
    //   // to be able to use fullwidth of metabar
    //   margin: 0 auto;
    // }

    @include mq($from: medium) {
      height: $headerMetabarHeightMediumUp;
    }
  }

  &__burger,
  &__search,
  &__metanav-item {
    color: $colorGrey90;
    transition: color $baseTransitionAnimationSpeed;
    cursor: pointer;

    &:hover {
      color: $colorBlack;
    }
  }

  &__burger {
    @include font('heavy');
    @include fontSize('milli');
    display: flex;
    align-items: center;
    appearance: none;
    background-color: transparent;
    border: none;
    padding: 0;
    margin-right: space('medium');
    gap: space('medium');
    color: $colorPrimary70;

    @include mq($from: small) {
      margin-right: space('xxlarge');
    }

    @include mq($from: medium) {
      margin-right: space('xxxxlarge');
    }
  }

  &__burger-icon {
    &.is-open {
      display: none;
    }
  }

  &__burger.is-open &__burger-icon.is-open {
    display: inline-flex;
  }

  &__burger.is-open &__burger-icon.is-close {
    display: none;
  }

  &__metabar-search {
    overflow: hidden; // prevents input from going out in smaller viewports
    margin-right: space('medium');
    transition-property: width;
    transition-duration: $baseTransitionAnimationSpeed;
    width: 40px;
    max-width: 345px;

    &:focus-within {
      width: 100%;
    }

    @include mq($from: small) {
      width: 96px;
      margin-right: space('xxlarge');
    }

    @include mq($from: medium) {
      // flex-shrink: 0; // prevent shrinking when metanav is in way
      width: 160px;
      margin-left: 0;
      margin-right: space('xxxxlarge');
    }
  }

  &__search {
    position: relative;
    display: block;
    width: 100%;
  }

  &__search-input {
    // width: 96px;
    width: 100%;
    max-width: 100%;
    background-color: transparent;
    color: transparent;
    font-family: inherit;
    font-size: inherit;
    border: none;
    padding: space('xsmall');
    padding-left: 0;
    padding-right: 0;
    height: $formInputHeightSmall;
    transition-property: padding-left, padding-right, background-color, color;
    transition-duration: $baseTransitionAnimationSpeed;

    @include mq($from: small) {
      color: $colorSecondary60;
      padding-left: calc(#{iconSize()} + #{space('medium')});
    }

    &:focus {
      // width: 345px;
      color: $colorSecondary60;
      background-color: $colorWhite;
      padding-left: calc(#{space('medium')} + #{iconSize()} + #{space('medium')});
      padding-right: space('xlarge');
    }

    // prevent ugly autofill background color in chrome
    &:-webkit-autofill {
      background-clip: text;
    }

    // hide search clear icon on chrome
    &[type="search"]::-webkit-search-decoration,
    &[type="search"]::-webkit-search-cancel-button,
    &[type="search"]::-webkit-search-results-button,
    &[type="search"]::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }
  }

  &__search-input:focus ~ &__search-icon,
  &__search-input:focus ~ &__search-text {
    transform: translate(space('medium'), -50%);
  }

  &__search-input:focus ~ &__search-text {
    color: $colorGrey70;
  }

  &__search-text,
  &__search-icon {
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    transition: transform $baseTransitionAnimationSpeed;
  }

  &__search-icon {
    left: 0px;

    @include mq($to: small) {
      transform: translate((40px - 24px) / 2, -50%);
    }
  }

  &__search-text {
    pointer-events: none;
    left: calc(#{iconSize()} + #{space('medium')});

    @include mq($to: small) {
      @include visuallyhidden;
    }
  }

  &__metabar-nav {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: space('medium');

    @include mq($from: small) {
      gap: space('xlarge');
    }

    @include mq($from: medium) {
      gap: space('xxlarge');
    }
  }

  &__metanav-popover {
    --width: auto;
    --min-width: 254px;

    &.is-langnav {
      --min-width: 140px;
    }
  }

  &__metanav-item {
    display: none;
    gap: space('small');
    align-items: center;
    justify-content: center;
    appearance: none;
    background-color: transparent;
    padding: 0;
    border: none;
    text-decoration: none;
    font-size: 100%;
    font-family: inherit;
    white-space: nowrap;
    min-width: 40px;

    @include mq($from: medium) {
      display: flex;
    }

    &[aria-expanded="true"] {
      color: $colorPrimary70;
    }

    &.is-heavy {
      @include font('heavy');
    }

    &.is-stay {
      display: flex;
    }

    &.is-popover-mobile {
      @include mq($from: medium) {
        display: none;
      }
    }

    &.is-popover-desktop {
      display: none;

      @include mq($from: medium) {
        display: flex;
      }
    }
  }

  &__metanav-menu-item {
    &.is-heavy {
      @include font('heavy');
    }
  }

  &__metanav-item-text {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &.is-vhidden {
      @include visuallyhidden;
    }

    &.has-no-short-label {
      @include mq($to: medium) {
        @include visuallyhidden;
      }
    }
  }

  &__metanav-item-text-label-long {
    @include mq($to: medium) {
      @include visuallyhidden;
    }
  }

  &__metanav-item-text-label-short {
    display: none;
    @include mq($from: small) {
      display: flex;
    }
    @include mq($from: medium) {
      display: none;
    }
  }

  &__metanav-icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__metanav-icon-badge {
    position: absolute;
    top: -4px;
    right: -4px;
  }

  &__metanav-icon {
    &.is-open {
      display: none;
    }
  }

  &__metanav-item[aria-expanded="true"] &__metanav-icon.is-open {
    display: inline-flex;
  }

  &__metanav-item[aria-expanded="true"] &__metanav-icon.is-close {
    display: none;
  }

  // &__metabar-login {
  //   display: flex;
  //   flex-basis: 50%;

  //   &:not(:empty) {
  // 		@include mq($from: medium) {
  //       flex-basis: auto;
  //       margin-left: space('small');
  //     }
  //   }
  // }

  // &__metabar-langnav {
  //   &:not(:empty) {
  //     @include mq($from: medium) {
  //       margin-left: space('medium');
  //     }
  //   }
  // }

  &__logobar {
    display: flex;

    @include mq($from: ultra) {
      margin-left: auto;
      margin-right: auto;
      width: $containerMaxWidth;
    }
  }

  &__logobar-logo {
    box-sizing: content-box;
    display: flex;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__logobar-decoration {
    background-color: var(--logobar-background-color);
    flex-grow: 1;

    @include mq($from: ultra) {
      width: calc((100% - 553px) + ((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2));
      margin-right: calc((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / -2);
    }
  }

  &__logo-link {
    @include spaceCurve('padding-left', 'regular');
    @include spaceCurve('padding-right', 'regular');
    padding-top: 14px;
    padding-bottom: 11px;
    padding-left: $containerMargin;
    display: inline-flex;
    width: 288px;
    height: 66px;
    transition: opacity $baseTransitionAnimationSpeed;

    @include mq($from: small) {
      width: 300px;
      padding-left: $containerMarginSmall;
    }

    @include mq($from: medium) {
      width: 399px;
      height: 88px;
      padding-top: 18px;
      padding-bottom: 18px;
      padding-left: $containerMarginMedium;
    }

    @include mq($from: large) {
      width: 617px;
      height: 135px;
      padding-top: 28px;
      padding-bottom: 24px;
      padding-left: $containerMarginLarge;
    }

    @include mq($from: ultra) {
      width: 553px;
      padding-left: 0;
    }
  }

  &__flyout,
  &__flyout-search {
    @include fontSize('milli');
    z-index: calc(#{$zIndexHeader} - 1);
    position: fixed;
    visibility: hidden;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100dvw;
    height: 100dvh;
    overflow: auto;
    transition: visibility $baseTransitionAnimationSpeed;
  }

  &__flyout {
    @media (min-height: 580px) {
      overflow: hidden;
    }

    @include mq($from: medium) {
      overflow: hidden;
    }
  }

  &__flyout-backdrop {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: $colorBlack40op;
    opacity: 0;
    transition: opacity $baseTransitionAnimationSpeed;
  }

  &__menu {
    position: absolute;
    width: 100%;
    height: auto;
    background-color: $colorSecondary30;
    display: grid;
    grid-template-rows: auto auto;
    transition-property: opacity, transform;
    transition-duration: $baseTransitionAnimationSpeed;
    transform: translateX(-100%);
    opacity: 0;

    @media (min-height: 580px) {
      height: 100%;
      grid-template-rows: minmax(0, 1fr) auto;
    }

    @include mq($from: medium) {
      height: 100%;
      grid-template-rows: minmax(0, 1fr) auto;
    }

    @include mq($from: medium) {
      width: 335px;
    }

    @include mq($from: large) {
      width: 374px;
    }

    @include mq($from: large) {
      width: 439px;
    }

    @include mq($from: ultra) {
      width: calc((439px - #{$containerMarginLarge}) + ((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2))
    }
  }

  &__menu-nav {
    position: relative;
    overflow-x: hidden;
    overflow-y: hidden;

    @media (min-height: 580px) {
      overflow-y: auto;
    }

    @include mq($from: medium) {
      overflow-y: auto;
      position: static;
    }
  }

  &__menu-metanav {
    @include spaceCurve('padding-top', 'tiny');
    @include spaceCurve('padding-bottom', 'medium');
    background-color: $colorSecondary20;

    @include mq($from: medium) {
      display: none;
    }
  }

  &__menu-metanav-nav {
    max-width: 375px;
  }

  &__menu-list,
  &__menu-metanav-list {
    display: block;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__menu-list,
  &__menu-metanav-list.is-level-2 {
    padding-top: calc(var(--stzh-header-main-height) + #{space('xxxlarge')});
    padding-bottom: space('xxxlarge');

    @include mq($from: medium) {
      padding-top: calc(var(--stzh-header-main-height) + #{space('huge')});
    }
  }

  &__menu-list.is-level-2,
  &__menu-metanav-list.is-level-2 {
    overflow: auto;
    background-color: $colorSecondary20;
    visibility: hidden;
    opacity: 0;
    transform: translateX(100%);
    position: absolute;
    z-index: 200;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition-property: opacity, visibility, transform;
    transition-duration: $baseTransitionAnimationSpeed;

    @include mq($from: medium) {
      z-index: initial;
      width: 315px;
      transform: none;
      left: 100%;
      // transition-property: opacity, visibility, transform;
      transition-property: opacity, visibility;
    }

    @include mq($from: large) {
      width: 334px;
    }

    @include mq($from: ultra) {
      width: 345px;
    }
  }

  &__menu-list.is-level-2 {
    height: max-content;

    @media (min-height: 580px) {
      height: 100%;
    }

    @include mq($from: medium) {
      height: 100%;
    }
  }

  &__menu-metanav-list.is-level-2 {
    background-color: $colorSecondary30;
    transform: none;
    transition-property: opacity, visibility;
  }

  // we add closing transition delay only to menu-list if hovering/focusing of another is currently active
  // to prevent showing background (flickering) when transitioning from one menu-list to another (on the same level)
  &__menu-list:where(:has(> #{&}__menu-list-item:hover > #{&}__menu-list) > #{&}__menu-list-item > #{&}__menu-list),
  &__menu-list:where(:has(> #{&}__menu-list-item.is-open > #{&}__menu-list) > #{&}__menu-list-item > #{&}__menu-list) {
    @include mq($from: medium) {
      transition-delay: $baseTransitionAnimationSpeed;
    }
  }

  &__menu-list-item:hover > &__menu-list,
  &__menu-list-item.is-open > &__menu-list {
    @include mq($from: medium) {
      z-index: 200;
      transition-delay: 0ms;
      visibility: visible;
      opacity: 1;
      // transform: translateX(0);
    }
  }

  &__menu-list-item:hover > &__menu-item,
  &__menu-list-item.is-open > &__menu-item {
    @include mq($from: medium) {
      background-color: $colorSecondary40;
    }

    &.is-level-2 {
      @include mq($from: medium) {
        background-color: $colorSecondary10;
      }
    }
  }

  &__menu-nav:has(#{&}__menu-list-item.is-open), // hide menu when any menu-list has been opened
  &__menu:has(#{&}__menu-metanav-list-item.is-open) &__menu-nav, // hide menu when menu metanav menu-list has been opened
  &__menu-list:has(#{&}__menu-list-item.is-open), // hide parent menu-list when sub menu-list has been opened
  &__menu-metanav-list:has(#{&}__menu-metanav-list-item.is-open) { // hide parent metanav menu-list when sub menu-list has been opened
    @include mq($to: medium) {
      transition-property: visibility;
      transition-duration: 0ms;
      transition-delay: $baseTransitionAnimationSpeed;
      visibility: hidden;
      // background-color: red;
    }
  }

  // force showing menu-list that is currently open
  &__menu-list-item.is-open > :where(#{&}__menu-list),
  &__menu-metanav-list-item.is-open > :where(#{&}__menu-metanav-list) {
    @include mq($to: medium) {
      transform: translateX(0);
      opacity: 1;
      visibility: visible;
      // background-color: green;
    }
  }

  &__menu-list-item,
  &__menu-metanav-list-item {
    display: grid;
  }

  &__menu-list-item {
    &.is-backlink {
      display: block;
    }
  }

  &__menu-item,
  &__menu-metanav-item {
    @include font('heavy');
    @include fontSize('milli');
    text-align: left;
    display: flex;
    align-items: center;
    gap: space('xsmall');
    appearance: none;
    border: none;
    text-decoration: none;
    background-color: transparent;
    color: $colorPrimary70;
    padding-left: space('xxxlarge');
    padding-top: space('small');
    padding-bottom: space('small');
    padding-right: space('large');
    transition-property: color, background-color;
    transition-duration: $baseTransitionAnimationSpeed;
    cursor: pointer;
  }

  &__menu-item,
  &__menu-metanav-item.is-title {
    @include fontSize('deci');
    min-height: 56px;

    @include mq($from: medium) {
      padding-left: $containerMarginMedium;
    }

    @include mq($from: large) {
      padding-left: $containerMarginLarge;
    }
  }

  &__menu-item {
    &.is-level-1 {
      @include mq($from: ultra) {
        padding-left: calc(((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2));
      }
    }

    &.has-items {
      @include mq($from: medium) {
        cursor: default;
      }
    }

    &.is-level-2 {
      @include fontSize('milli');
      min-height: 48px;

      @include mq($from: medium) {
        padding-left: space('xlarge');
      }

      @include mq($from: ultra) {
        padding-left: space('xxlarge');
      }
    }

    &.is-level-2.is-backlink {
      @include font;
      @include fontSize('micro');
      @include spaceCurve('margin-bottom', 'regular');
      @include spaceCurve('padding-top', 'regular');
      @include spaceCurve('padding-bottom', 'small');
      min-height: none;
      padding-left: $containerMargin;
      padding-right: $containerMargin;
      gap: space('xxsmall');

      @include mq($from: small) {
        padding-left: $containerMarginSmall;
        padding-right: $containerMarginSmall;
      }

      @include mq($from: medium) {
        display: none;
      }
    }

    &.is-level-2.is-main {
      @include fontSize('centi');
      margin-bottom: space('xlarge');
    }
  }

  &__menu-metanav-item {
    &.is-level-1 {
      min-height: 44px;
      padding-top: space('xsmall');
      padding-bottom: space('xsmall');
    }

    &.is-level-2 {
      min-height: 56px;
      gap: space('medium');
      padding-right: space('xxlarge');
    }

    &.is-title {
      @include font('heavy');
      justify-content: space-between;
      padding-top: 0;
      padding-bottom: 0;
      padding-right: space('medium');
      margin-bottom: space('xlarge');
      cursor: default;
    }

    &.is-language {
      @include font;
      justify-content: space-between;
    }

    &.is-action {
      display: grid;
      padding-right: space('xxxlarge');
      margin-top: space('xlarge');
    }

    &.is-hidden {
      display: none;
    }
  }

  &__menu-metanav-item.is-level-1 &__menu-metanav-item-icon {
    --size: #{iconSize('small')};
  }

  &__menu-metanav-item-text {
    display: flex;
    gap: space('xsmall');

    &.is-vhidden {
      @include visuallyhidden;
    }
  }

  &__menu-metanav-item-counter {
    @include font;
  }

  /* app-nav */

  &__app-nav {
    display: none;

    @include mq($from: medium) {
      display: block;
    }
  }

  &__app-nav-bottom {
    position: fixed;
    bottom: 0;
    left: 0;
    width: calc(100vw - var(--stzh-scrollbar-width));
    z-index: calc(#{$zIndexHeader} - 1);

    @include mq($from: medium) {
      display: none;
    }
  }

  /* Search is filled */

  &--is-search-filled &__search-text {
    @include visuallyhidden;
  }

  /* Hide logo */

  &--hide-logo &__logo-link {
    opacity: 0;
  }

  /* Fixed variant (as soon as header is passed) */

  &--is-fixed &__inner {
    @include mq($from: small) {
      transform: translateY(-100%);
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      margin-top: calc(var(--stzh-header-logobar-height) / -1);
    }
  }

  &--is-fixed &__main {
    @include mq($to: small) {
      transform: translateY(-100%);
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      margin-top: calc(var(--stzh-header-logobar-height) / -1);
    }
  }

  &--is-fixed-transition &__inner {
    @include mq($from: small) {
      transition-property: transform;
      transition-duration: $baseTransitionAnimationSpeed;
    }
  }

  &--is-fixed-transition &__main {
    @include mq($to: small) {
      transition-property: transform;
      transition-duration: $baseTransitionAnimationSpeed;
    }
  }

  /* Sticky (transition) variant */

  &--is-sticky-transition &__logobar {
    transition-property: opacity, visibility;
    transition-duration: $baseTransitionAnimationSpeed;
  }

  &--is-sticky-transition &__inner,
  &--is-sticky-transition &__main {
    transition-property: transform, margin-top;
    transition-duration: $baseTransitionAnimationSpeed;
  }

  &--is-sticky &__inner {
    @include mq($from: small) {
      transform: translateY(0);
    }
  }

  &--is-sticky &__main {
    @include mq($to: small) {
      transform: translateY(0);
    }
  }

  &--is-sticky:where(#{&}--has-empty-metabar-mobile) &__inner {
    @include mq($from: small, $to: medium) {
      margin-top: calc((var(--stzh-header-logobar-height) + var(--stzh-header-metabar-height)) / -1);
    }
  }

  &--is-sticky:where(#{&}--has-empty-metabar-mobile) &__main {
    @include mq($to: small) {
      margin-top: calc((var(--stzh-header-logobar-height) + var(--stzh-header-metabar-height)) / -1);
    }
  }

  // &--is-sticky:where(#{&}--has-empty-metabar-mobile) &__inner {
  // 	@include mq($from: small, $to: medium) {
  //     box-shadow: none;
  //   }
  // }

  // &--is-sticky:where(#{&}--has-empty-metabar-mobile) &__main {
  // 	@include mq($to: small) {
  //     box-shadow: none;
  //   }
  // }

  &--is-sticky:where(#{&}--has-empty-metabar-mobile) &__metabar {
    @include mq($to: medium) {
      visibility: hidden;
    }
  }


  &--is-sticky:where(#{&}--has-empty-metabar) &__inner {
    @include mq($from: small) {
      margin-top: calc((var(--stzh-header-logobar-height) + var(--stzh-header-metabar-height)) / -1);
    }
  }

  &--is-sticky:where(#{&}--has-empty-metabar) &__main {
    @include mq($to: small) {
      margin-top: calc((var(--stzh-header-logobar-height) + var(--stzh-header-metabar-height)) / -1);
    }
  }

  // &--is-sticky:where(#{&}--has-empty-metabar) &__inner {
  // 	@include mq($from: small) {
  //     box-shadow: none;
  //   }
  // }

  // &--is-sticky:where(#{&}--has-empty-metabar) &__main {
  // 	@include mq($to: small) {
  //     box-shadow: none;
  //   }
  // }

  &--is-sticky:where(#{&}--has-empty-metabar) &__metabar {
    visibility: hidden;
  }

  &--is-sticky &__logobar {
    visibility: hidden;
    opacity: 0;
  }

  // &--is-sticky:where(#{&}--has-metabar-stay) &__metabar {
  //   visibility: visible;
  // }

  // &--is-sticky:where(#{&}--has-metabar-stay) &__main {
  // 	@include mq($to: small) {
  //     // box-shadow: $boxShadowHeader;
  //     margin-top: calc((var(--stzh-header-logobar-height)) / -1);
  //   }
  // }

  // &--is-sticky:where(#{&}--has-metabar-stay) &__inner {
  // 	@include mq($from: small) {
  //     // box-shadow: $boxShadowHeader;
  //     margin-top: calc((var(--stzh-header-logobar-height)) / -1);
  //   }
  // }

  /* Sticky disabled */

  // &--sticky-disabled &__main {
  //   @include mq($from: medium) {
  //     padding-top: $headerMetabarHeightMediumUp;
  //   }
  // }

  /* Sticky always */

  &--sticky-always-full &__inner,
  &--sticky-always-full &__main {
    margin-top: 0px;
  }

  &--sticky-always-full &__logobar {
    visibility: visible;
    opacity: 1;
  }

  /* Flyout open */

  &__flyout-search-main {
    position: absolute;
    width: 100%;
    transition-property: opacity, transform;
    transition-duration: $baseTransitionAnimationSpeed;
    transform: translateY(-100px);
    opacity: 0;
    margin-top: var(--stzh-header-main-height);
    overflow: auto;
  }

  &--is-flyout-open &__flyout,
  &--is-flyout-search-open &__flyout-search {
    visibility: visible;
  }

  &--is-flyout-open &__flyout-backdrop,
  &--is-flyout-search-open &__flyout-backdrop {
    opacity: 1;
  }

  &--is-flyout-open &__flyout-scrollbar,
  &--is-flyout-search-open &__flyout-scrollbar {
    display: none;
    z-index: 999;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: var(--stzh-scrollbar-width);
    background-color: $colorGrey10;
    border-left: calc(0.5px * var(--stzh-scrollbar-active)) solid $colorGrey30;

    @media (min-height: 580px) {
      display: block;
    }

    @include mq($from: medium) {
      display: block;
    }
  }

  &--is-flyout-open &__menu {
    opacity: 1;
    transform: translateX(0);
  }

  &--is-flyout-search-open &__flyout-search-main {
    opacity: 1;
    transform: translateX(0);
  }

  &--is-flyout-open &__inner,
  &--is-flyout-search-open &__inner {
    @include mq($from: small) {
      z-index: $zIndexHeader;
      transform: translateY(0);
      position: fixed;
      top: 0;
      left: 0;
      right: var(--stzh-scrollbar-width);
      margin-top: 0;
    }
  }

  &--is-flyout-open &__main,
  &--is-flyout-search-open &__main {
    @include mq($to: small) {
      z-index: $zIndexHeader;
      transform: translateY(0);
      position: fixed;
      top: 0;
      left: 0;
      right: var(--stzh-scrollbar-width);
      margin-top: 0;
    }
  }

  &--is-flyout-open &__logobar,
  &--is-flyout-search-open &__logobar {
    opacity: 1;
  }

  &--is-flyout-open &__logobar,
  &--is-flyout-search-open &__logobar,
  &--is-flyout-open &__metabar,
  &--is-flyout-search-open &__metabar {
    visibility: visible;
  }

  &--is-flyout-open &__logo-link,
  &--is-flyout-search-open &__logo-link {
    opacity: 1;
    transition: none;
  }

  /**
   * Search button.
   */
  &__search-button--is-hidden {
    display: none;
  }
}
